import React, { useState, useImperativeHandle, forwardRef  } from "react";
import { Box, Button, Portal, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { keyframes } from "@mui/system";
import { useSnackbar } from "../../Context/Contexts";
interface ToolbarWithSaveProps {
    onSave?: () => void;
    onCancel?: () => void;
    isSaving?: boolean;
    hasChanges?: boolean;
    showHasChangesError?: boolean;
    setShowChangesError?: (bool :boolean) => void;
}
export interface ToolbarWithSaveHandle {
    showHasChangesError: () => void;
}

const shake = keyframes`
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
`;

const ToolbarWithSave = forwardRef<ToolbarWithSaveHandle, ToolbarWithSaveProps>(
    ({ onSave, onCancel, isSaving, hasChanges = false }, ref) => {
        const { t } = useTranslation();
        const { showSnackbar } = useSnackbar();
        const [isShaking, setIsShaking] = useState(false);

        const theme = useTheme();
        const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

        useImperativeHandle(ref, () => ({
            showHasChangesError() {
                showSnackbar(t("common.unsavedChanges"), "warning");
                setIsShaking(true);
                setTimeout(() => {
                    setIsShaking(false);
                }, 500);
            }
        }));

        const handleSaveClick = async () => {
            if (onSave) {
                await onSave();
            }
        };
        const content = (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, md: 1 },
                    position: { xs: "absolute", md: "fixed" },
                    top: { xs: 0, md: 8 },
                    right: { xs: 16, md: 16 },
                    height: { xs: 0, md: "auto" },
                    zIndex: { xs: 1000, md: theme.zIndex.modal + 1 },
                    border: "none",
                    px: { xs: 0, md: 1 },
                    py: { xs: 0, md: 6 },
                    animation: isShaking ? `${shake} 0.5s` : "none",
                }}
            >
                {hasChanges && onCancel && (
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={onCancel}
                        sx={{
                            textTransform: "none",
                            fontSize: { xs: "14px", md: "14px" },
                            padding: { xs: "4px 12px", md: "6px 14px" },
                            mr: { xs: 1, md: 1 },
                        }}
                    >
                        {t("common.cancel")}
                    </Button>
                )}
                {hasChanges && onSave && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSaveClick}
                        disabled={isSaving || !hasChanges}
                        sx={{
                            textTransform: "none",
                            fontSize: { xs: "14px", md: "14px" },
                            padding: { xs: "4px 12px", md: "6px 14px" },
                        }}
                    >
                        {isSaving ? t("common.saving") : t("common.save")}
                    </Button>
                )}
            </Box>
        );

        // Only portal on desktop
        return isDesktop ? <Portal container={document.body}>{content}</Portal> : content;


    }
);

export default ToolbarWithSave;
